// Shared wording that depends on what the app does (docs/creating-a-new-app.md section 6).
// - AI_PROCESSING: true for apps whose results come from AI models. Only those apps say
//   "AI" or mention third-party AI providers; every other app gets neutral sentences.
// - CREDITS_ENABLED: false for apps without credits. Shared screens then show no credit
//   balance, history, tile, chip or explainer.
// - RESULTS_DISCLAIMER and DATA_SECURITY_ANSWER replace those two sentences outright.
import config from "@/config";

export function aiProcessing() {
  return Boolean(config && config.AI_PROCESSING);
}

export function creditsEnabled() {
  return !(config && config.CREDITS_ENABLED === false);
}

// The legal line under the plans (/subscribe, /pricing).
export function resultsDisclaimer() {
  if (config && config.RESULTS_DISCLAIMER) return config.RESULTS_DISCLAIMER;
  return aiProcessing()
    ? "Results are produced by AI and can vary between runs. You are responsible for the content you upload and for how you use the results."
    : "You are responsible for the content you submit and for how you use the results.";
}

export function dataSecurityAnswer() {
  if (config && config.DATA_SECURITY_ANSWER) return config.DATA_SECURITY_ANSWER;
  return [
    "Your content travels over encrypted connections, is only accessible through your own account, and is never shared with other users or sold.",
    aiProcessing()
      ? "To provide AI features, your content is processed by third-party AI infrastructure providers solely to deliver the requested feature."
      : "",
    "See the Privacy Policy for how long content is kept."
  ]
    .filter(Boolean)
    .join(" ");
}

// What credits pay for, in the plan cards, the limits hint and the toolbar tooltip.
export function creditsPayFor() {
  return aiProcessing()
    ? "Credits pay for AI processing"
    : "Credits pay for the jobs you run";
}

// The history table on Billing and Subscription. An app with ACCOUNT_USAGE_GETTER names its
// usage history (ACCOUNT_USAGE_TITLE, e.g. MakeMyCarousel's "AI draft history").
export function usageHistoryTitle() {
  return (config && config.ACCOUNT_USAGE_TITLE) || "Usage history";
}

export function historyEmptyText(usage) {
  const noun = (config && config.UNIT_NOUN) || "job";
  return usage
    ? `Nothing here yet. Your history appears after your first ${noun}.`
    : "No credit activity yet. Credits you receive, buy or use appear here.";
}

// The default answer to "What are credits, and do they expire?".
export function creditsExplainer() {
  const product = config && config.PRODUCT_NAME;
  const purpose = aiProcessing()
    ? "pay for AI processing on"
    : "pay for the jobs you run on";
  return `Credits are usage units that ${purpose} ${product} - not money, stored value, or transferable, and they can't be withdrawn or redeemed for cash. Each job spends credits based on run time and compute. Included monthly credits reset each billing cycle and do not roll over.`;
}
